import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Swal from 'sweetalert2';
import { API, useAuth } from '../contexts/AuthContext';

function formatNumero(numero) {
  if (!numero) return '';
  const limpo = String(numero).replace(/\D/g, '');
  if (limpo.length === 13) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 9)}-${limpo.slice(9)}`;
  }
  if (limpo.length === 12) {
    return `+${limpo.slice(0, 2)} (${limpo.slice(2, 4)}) ${limpo.slice(4, 8)}-${limpo.slice(8)}`;
  }
  return numero;
}

function formatDataBR(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('pt-BR');
}

const FORM_VAZIO = { canal_id: '', numero: '', push_name: '', filial: '', setor: '', pular_fluxo: false };

export default function Contatos() {
  const { temPermissao } = useAuth();
  const podeUsar = temPermissao('ti:atendimentos');

  const [contatos, setContatos] = useState([]);
  const [canais, setCanais] = useState([]);
  const [filiais, setFiliais] = useState([]);
  const [setores, setSetores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busca, setBusca] = useState('');
  const [erro, setErro] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState('');

  const carregar = async () => {
    setLoading(true);
    try {
      const [r1, r2, r3, r4] = await Promise.all([
        axios.get(`${API}/contatos`, { params: busca ? { busca } : {} }),
        axios.get(`${API}/atendimento/canais`),
        axios.get(`${API}/atendimento/filiais`, { params: { ativos: 'true' } }).catch(() => ({ data: { data: [] } })),
        axios.get(`${API}/atendimento/setores`, { params: { ativos: 'true' } }).catch(() => ({ data: { data: [] } })),
      ]);
      setContatos(r1.data?.data || []);
      setCanais(r2.data?.data || []);
      setFiliais(r3.data?.data || []);
      setSetores(r4.data?.data || []);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.message || 'Nao foi possivel carregar os dados.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!podeUsar) { setLoading(false); return; }
    carregar();
  }, [podeUsar, busca]);

  const abrirNovo = () => {
    setEditando(null);
    setForm({ ...FORM_VAZIO, canal_id: canais[0]?.id || '' });
    setErroForm('');
    setModalAberto(true);
  };

  const abrirEditar = (contato) => {
    setEditando(contato);
    setForm({
      canal_id: contato.canal_id,
      numero: contato.numero || '',
      push_name: contato.push_name || '',
      filial: contato.filial || '',
      setor: contato.setor || '',
      pular_fluxo: !!contato.pular_fluxo,
    });
    setErroForm('');
    setModalAberto(true);
  };

  const salvar = async () => {
    if (!editando && !form.numero) { setErroForm('Informe o numero.'); return; }
    const dados = {
      push_name: form.push_name.trim(),
      filial: form.filial.trim() || null,
      setor: form.setor.trim() || null,
    };
    if (!dados.push_name) { setErroForm('Nome e obrigatorio.'); return; }
    setSalvando(true);
    setErroForm('');
    try {
      if (editando) {
        await axios.put(`${API}/contatos/${editando.id}`, {
          push_name: dados.push_name,
          filial: dados.filial,
          setor: dados.setor,
          pular_fluxo: form.pular_fluxo,
        });
      } else {
        await axios.post(`${API}/contatos`, {
          canal_id: form.canal_id ? Number(form.canal_id) : null,
          numero: form.numero,
          push_name: dados.push_name,
          filial: dados.filial,
          setor: dados.setor,
        });
      }
      setModalAberto(false);
      await carregar();
    } catch (e) {
      setErroForm(e.response?.data?.message || 'Nao foi possivel salvar.');
    } finally {
      setSalvando(false);
    }
  };

  const excluir = async (contato) => {
    const ok = await Swal.fire({
      icon: 'warning',
      title: 'Excluir contato?',
      text: `${contato.push_name || formatNumero(contato.numero)} sera removido.`,
      showCancelButton: true,
      confirmButtonText: 'Excluir',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#b43737',
    });
    if (!ok.isConfirmed) return;
    try {
      await axios.delete(`${API}/contatos/${contato.id}`);
      await carregar();
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro', text: e.response?.data?.message || e.message });
    }
  };

  if (!podeUsar) {
    return (
      <div className="content-shell">
        <div className="feedback-banner feedback-banner--error">
          Voce nao tem permissao para acessar contatos.
        </div>
      </div>
    );
  }

  return (
    <div className="content-shell">
      <section className="page-header">
        <div>
          <span className="section-header__eyebrow">TI</span>
          <h1>Contatos WhatsApp</h1>
          <p>Cadastre e gerencie contatos. Apenas nome e obrigatorio; filial e setor sao opcionais e ajudam o bot a pular etapas no retorno.</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <a
            href="/ti/atendimentos"
            className="btn btn-sm btn-outline-secondary"
            title="Voltar para a lista de conversas"
          >
            <i className="bi bi-chat-left-text me-1" /> Conversas
          </a>
          <button
            type="button"
            className="btn btn-sm"
            onClick={abrirNovo}
            style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
          >
            <i className="bi bi-plus-lg me-1" /> Novo contato
          </button>
        </div>
      </section>

      <div className="filter-card">
        <div className="filter-card__field" style={{ flex: 1 }}>
          <input
            type="search"
            placeholder="Buscar por nome ou numero..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </div>

      {erro && <div className="feedback-banner feedback-banner--error">{erro}</div>}

      <section className="content-card">
        {loading ? (
          <div className="content-card--centered">
            <div className="screen-loader__spinner" />
            <p>Carregando contatos...</p>
          </div>
        ) : contatos.length === 0 ? (
          <div className="empty-state">
            <strong>Nenhum contato cadastrado</strong>
            <span>Clique em "Novo contato" para comecar.</span>
          </div>
        ) : (
          <div className="table-shell">
            <table className="portal-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Numero</th>
                  <th>Filial</th>
                  <th>Setor</th>
                  <th>Origem</th>
                  <th>Cadastrado em</th>
                  <th style={{ textAlign: 'right' }}>Acoes</th>
                </tr>
              </thead>
              <tbody>
                {contatos.map((c) => {
                  const canal = canais.find((k) => k.id === c.canal_id);
                  // Bot so faz intake quando falta NOME; filial/setor sao
                  // preenchidos pelo atendente e nao disparam o alerta.
                  const semNome = !c.push_name;
                  return (
                    <tr key={c.id}>
                      <td>
                        <span className="cell-strong">
                          {c.push_name || <span className="cell-muted">(sem nome)</span>}
                        </span>
                        {semNome && (
                          <span
                            title="Contato sem nome - bot fara a identificacao na proxima conversa"
                            style={{
                              marginLeft: 6, fontSize: '0.7rem',
                              color: '#fe8d25', fontWeight: 600,
                            }}
                          >
                            <i className="bi bi-exclamation-triangle" />
                          </span>
                        )}
                      </td>
                      <td className="cell-muted">{formatNumero(c.numero)}</td>
                      <td className="cell-muted">{c.filial || '-'}</td>
                      <td className="cell-muted">{c.setor || '-'}</td>
                      <td className="cell-muted">{canal?.nome || (c.canal_id ? `#${c.canal_id}` : '-')}</td>
                      <td className="cell-muted">{formatDataBR(c.criado_em)}</td>
                      <td style={{ textAlign: 'right' }}>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary me-1"
                          onClick={() => abrirEditar(c)}
                          title="Editar"
                        >
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => excluir(c)}
                          title="Excluir contato"
                        >
                          <i className="bi bi-trash" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {modalAberto && (
        <div
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(8, 23, 43, 0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            className="content-card"
            style={{
              width: 'min(520px, 96vw)', padding: 0, display: 'flex',
              flexDirection: 'column', overflow: 'hidden',
            }}
          >
            <header style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', borderBottom: '1px solid var(--portal-border)',
            }}>
              <strong>
                <i className={`bi ${editando ? 'bi-pencil' : 'bi-plus-circle'} me-2`} />
                {editando ? 'Editar contato' : 'Novo contato'}
              </strong>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setModalAberto(false)}
                disabled={salvando}
              >
                <i className="bi bi-x-lg" />
              </button>
            </header>

            <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
              {erroForm && (
                <div className="feedback-banner feedback-banner--error" style={{ fontSize: '0.85rem' }}>
                  {erroForm}
                </div>
              )}

              {!editando && (
                <>
                  <div>
                    <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                      Canal de origem (opcional)
                    </label>
                    <select
                      className="form-select"
                      value={form.canal_id}
                      onChange={(e) => setForm({ ...form, canal_id: e.target.value })}
                    >
                      <option value="">Nao informar</option>
                      {canais.map((c) => (
                        <option key={c.id} value={c.id}>{c.nome}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                      Numero (com DDD)
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.numero}
                      onChange={(e) => setForm({ ...form, numero: e.target.value })}
                      placeholder="Ex: 21965868915 ou 5521965868915"
                    />
                  </div>
                </>
              )}

              <div>
                <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                  Nome <span style={{ color: '#dc3545' }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={form.push_name}
                  onChange={(e) => setForm({ ...form, push_name: e.target.value })}
                  placeholder="Como o contato deve aparecer"
                  autoFocus
                />
              </div>

              <div className="row g-3" style={{ margin: 0 }}>
                <div className="col-12 col-sm-6" style={{ paddingLeft: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Filial (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={form.filial}
                    onChange={(e) => setForm({ ...form, filial: e.target.value })}
                  >
                    <option value="">Nao informar</option>
                    {form.filial && !filiais.some((f) => f.nome === form.filial) && (
                      <option value={form.filial}>{form.filial}</option>
                    )}
                    {filiais.map((f) => (
                      <option key={f.id || f.nome} value={f.nome}>
                        {f.codigo ? `${f.codigo} - ${f.nome}` : f.nome}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-12 col-sm-6" style={{ paddingRight: 0 }}>
                  <label style={{ fontSize: '0.78rem', color: 'var(--portal-muted)' }}>
                    Setor (opcional)
                  </label>
                  <select
                    className="form-select"
                    value={form.setor}
                    onChange={(e) => setForm({ ...form, setor: e.target.value })}
                  >
                    <option value="">Nao informar</option>
                    {form.setor && !setores.some((s) => s.nome === form.setor) && (
                      <option value={form.setor}>{form.setor}</option>
                    )}
                    {setores.map((s) => (
                      <option key={s.id || s.nome} value={s.nome}>{s.nome}</option>
                    ))}
                  </select>
                </div>
              </div>

              <label
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, marginTop: 16,
                  padding: '10px 12px', borderRadius: 10,
                  background: 'var(--portal-surface-soft)', border: '1px solid var(--portal-border)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={!!form.pular_fluxo}
                  onChange={(e) => setForm({ ...form, pular_fluxo: e.target.checked })}
                />
                <span style={{ fontSize: '0.85rem', color: 'var(--portal-text)' }}>
                  Ignorar fluxo do bot
                  <br />
                  <span style={{ fontSize: '0.75rem', color: 'var(--portal-muted)' }}>
                    As mensagens deste contato vao direto para a fila, sem passar pelo bot.
                  </span>
                </span>
              </label>
            </div>

            <footer style={{
              padding: '12px 18px', borderTop: '1px solid var(--portal-border)',
              display: 'flex', justifyContent: 'flex-end', gap: 8,
            }}>
              <button
                type="button"
                className="btn btn-sm btn-outline-secondary"
                onClick={() => setModalAberto(false)}
                disabled={salvando}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={salvar}
                disabled={salvando}
                style={{ background: '#2A8A9C', color: '#fff', border: 'none' }}
              >
                <i className="bi bi-check2 me-1" />
                {salvando ? 'Salvando...' : 'Salvar'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
